import {Checkbox} from '@dropbox/dig-components/controls';
import {Menu} from '@dropbox/dig-components/menu';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  ArrowDownLine,
  ArrowUpLine,
  CheckmarkLine,
  FilterLine,
  SettingsLine,
} from '@dropbox/dig-icons/assets';
import type {SortOption} from 'components/DSYS/TableToolbar';
import {t} from 'i18next';
import type {ReactNode} from 'react';
import {
  type ToolbarMenuTriggerProps,
  ToolbarOutlineIconButton,
} from 'views/workstreams/detail/ToolbarOutlineIconButton';

import type {RoadmapGroupingOption} from './grouping';
import type {RoadmapGrouping, RoadmapSortOrder, RoadmapViewMode} from './types';

const ROADMAP_SETTINGS_MENU_Z_INDEX = 20;

export const RoadmapSettingsMenuRow = ({
  label,
  value,
  icon,
}: {
  label: string;
  value?: ReactNode;
  icon?: ReactNode;
}) => (
  <Split alignY="center" gap="8" style={{width: '100%'}}>
    {icon ? (
      <Split.Item>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          style={{width: 24, height: 24, lineHeight: 0}}
        >
          {icon}
        </Box>
      </Split.Item>
    ) : null}
    <Split.Item width="fill">
      <Text>{label}</Text>
    </Split.Item>
    {value ? (
      <Split.Item>
        <Text size="small" color="subtle">
          {value}
        </Text>
      </Split.Item>
    ) : null}
  </Split>
);

export const RoadmapSettingsMenu = ({
  statusOptions,
  selectedStatuses,
  onToggleStatus,
  onClearStatusFilters,
  viewMode,
  onViewModeChange,
  sortOptions,
  sortColumn,
  sortDirection,
  onSortChange,
  groupingOptions,
  selectedGrouping,
  onGroupingChange,
}: {
  statusOptions: string[];
  selectedStatuses: Set<string>;
  onToggleStatus: (status: string) => void;
  onClearStatusFilters: () => void;
  viewMode: RoadmapViewMode;
  onViewModeChange: (mode: RoadmapViewMode) => void;
  sortOptions: SortOption[];
  sortColumn: RoadmapSortOrder;
  sortDirection: 'asc' | 'desc';
  onSortChange: (config: {sortColumn: RoadmapSortOrder; sortDirection: 'asc' | 'desc'}) => void;
  groupingOptions: RoadmapGroupingOption[];
  selectedGrouping: RoadmapGrouping;
  onGroupingChange: (grouping: RoadmapGrouping) => void;
}) => {
  const activeFilterCount = selectedStatuses.size;
  const activeSortOption = sortOptions.find((option) => option.id === sortColumn);
  const activeGroupingOption = groupingOptions.find((option) => option.value === selectedGrouping);

  return (
    <Menu.Wrapper closeOnSelection={false}>
      {({getContentProps, getTriggerProps}) => (
        <>
          <ToolbarOutlineIconButton
            icon={SettingsLine}
            count={activeFilterCount}
            ariaLabel={t('settings')}
            isCompact={false}
            isHighlighted={activeFilterCount > 0}
            hideCountWhenZero
            ariaPressed={activeFilterCount > 0}
            triggerProps={getTriggerProps() as ToolbarMenuTriggerProps}
          />
          <Menu.Content
            {...getContentProps()}
            placement="bottom-end"
            style={{minWidth: 300, zIndex: ROADMAP_SETTINGS_MENU_Z_INDEX}}
          >
            <Menu.Segment withLabel={t('filter')}>
              <Menu.Submenu
                closeOnSelection={false}
                withTriggerContent={
                  <RoadmapSettingsMenuRow
                    label={t('status')}
                    value={activeFilterCount > 0 ? activeFilterCount : undefined}
                    icon={<UIIcon src={FilterLine} size="medium" />}
                  />
                }
              >
                <Menu.Segment>
                  {statusOptions.map((status) => (
                    <Menu.ActionItem
                      key={status}
                      onClick={() => onToggleStatus(status)}
                      withLeftAccessory={
                        <Checkbox checked={selectedStatuses.has(status)} readOnly tabIndex={-1} />
                      }
                    >
                      {status}
                    </Menu.ActionItem>
                  ))}
                </Menu.Segment>
              </Menu.Submenu>
            </Menu.Segment>

            {activeFilterCount > 0 ? (
              <Menu.Segment>
                <Menu.ActionItem onClick={onClearStatusFilters}>
                  <Text color="subtle">{t('clear_all_filters')}</Text>
                </Menu.ActionItem>
              </Menu.Segment>
            ) : null}

            <Menu.Segment withLabel={t('display')}>
              <Menu.Submenu
                closeOnSelection={false}
                withTriggerContent={
                  <RoadmapSettingsMenuRow
                    label={t('view')}
                    value={viewMode === 'table' ? 'Table' : 'Gantt'}
                  />
                }
              >
                <Menu.Segment>
                  {(['table', 'gantt'] as const).map((mode) => {
                    const label = mode === 'table' ? 'Table' : 'Gantt';
                    return (
                      <Menu.ActionItem
                        key={mode}
                        onClick={() => onViewModeChange(mode)}
                        withRightAccessory={
                          viewMode === mode ? (
                            <UIIcon src={CheckmarkLine} size="small" />
                          ) : undefined
                        }
                        withTitle={label}
                      />
                    );
                  })}
                </Menu.Segment>
              </Menu.Submenu>

              <Menu.Submenu
                closeOnSelection={false}
                withTriggerContent={
                  <RoadmapSettingsMenuRow
                    label={t('grouping')}
                    value={activeGroupingOption?.label ?? selectedGrouping}
                  />
                }
              >
                <Menu.Segment>
                  {groupingOptions.map((option) => (
                    <Menu.ActionItem
                      key={option.value}
                      onClick={() => onGroupingChange(option.value)}
                      withRightAccessory={
                        selectedGrouping === option.value ? (
                          <UIIcon src={CheckmarkLine} size="small" />
                        ) : undefined
                      }
                      withTitle={option.label}
                    />
                  ))}
                </Menu.Segment>
              </Menu.Submenu>

              {viewMode === 'table' ? (
                <Menu.Submenu
                  closeOnSelection={false}
                  withTriggerContent={
                    <div
                      style={{width: '100%'}}
                      onClick={(event) => {
                        event.stopPropagation();
                        onSortChange({
                          sortColumn,
                          sortDirection: sortDirection === 'asc' ? 'desc' : 'asc',
                        });
                      }}
                    >
                      <RoadmapSettingsMenuRow
                        label={t('ordering')}
                        value={
                          <Split alignY="center" gap="4">
                            <Split.Item>
                              <span className={atoms({display: 'flex', alignItems: 'center'})}>
                                <UIIcon
                                  src={sortDirection === 'asc' ? ArrowUpLine : ArrowDownLine}
                                  size="small"
                                  className={atoms({color: 'Text Subtle'})}
                                />
                              </span>
                            </Split.Item>
                            <Split.Item>{activeSortOption?.label ?? sortColumn}</Split.Item>
                          </Split>
                        }
                      />
                    </div>
                  }
                >
                  <Menu.Segment>
                    {sortOptions.map((option) => {
                      const isActive = option.id === sortColumn;
                      return (
                        <Menu.ActionItem
                          key={option.id}
                          onClick={() =>
                            onSortChange({
                              sortColumn: option.id as RoadmapSortOrder,
                              sortDirection: isActive
                                ? sortDirection === 'asc'
                                  ? 'desc'
                                  : 'asc'
                                : 'asc',
                            })
                          }
                          withRightAccessory={
                            isActive ? (
                              <Split gap="4" alignY="center">
                                <Split.Item>
                                  <UIIcon
                                    src={sortDirection === 'asc' ? ArrowUpLine : ArrowDownLine}
                                    size="small"
                                  />
                                </Split.Item>
                                <Split.Item>
                                  <UIIcon src={CheckmarkLine} size="small" />
                                </Split.Item>
                              </Split>
                            ) : undefined
                          }
                          withTitle={option.label}
                        />
                      );
                    })}
                  </Menu.Segment>
                </Menu.Submenu>
              ) : null}
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};
